import React, { useState } from 'react';
import { Sparkles, ExternalLink, X, Gem, Award } from 'lucide-react';
import { Button } from './ui/button';
import { nftData } from '../data/mock';

export const NFTGallery = () => {
  const [selectedNFT, setSelectedNFT] = useState(null);

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'Legendary':
        return 'from-yellow-500 to-orange-500';
      case 'Rare':
        return 'from-purple-500 to-pink-500';
      default:
        return 'from-blue-500 to-cyan-500';
    }
  };

  const getRarityBg = (rarity) => {
    switch (rarity) {
      case 'Legendary':
        return 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400';
      case 'Rare':
        return 'bg-purple-500/20 border-purple-500/50 text-purple-400';
      default:
        return 'bg-blue-500/20 border-blue-500/50 text-blue-400';
    }
  };

  return (
    <>
      {/* NFT Viewer Modal */}
      {selectedNFT && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
          <div className="relative max-w-5xl w-full bg-gunmetal-900 rounded-2xl overflow-hidden border border-purple-500/30">
            <button
              onClick={() => setSelectedNFT(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 hover:bg-accent-red rounded-full flex items-center justify-center text-white transition-colors"
            >
              <X size={24} />
            </button>
            
            <div className="flex flex-col lg:flex-row">
              {/* Image */}
              <div className="lg:w-2/3 bg-black">
                <img
                  src={selectedNFT.image}
                  alt={selectedNFT.title}
                  className="w-full h-full object-contain max-h-[70vh]"
                />
              </div>
              
              {/* Details */}
              <div className="lg:w-1/3 p-6 flex flex-col">
                <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full border text-xs font-bold uppercase mb-4 w-fit ${getRarityBg(selectedNFT.rarity)}`}>
                  {selectedNFT.rarity === 'Legendary' ? <Gem size={14} /> : <Award size={14} />}
                  <span>{selectedNFT.rarity}</span>
                </div>
                
                <h3 className="text-2xl font-black text-white mb-2">{selectedNFT.title}</h3>
                <p className="text-steel-400 mb-4">{selectedNFT.description}</p>
                
                <div className="mt-auto space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-steel-500">Collection</span>
                    <span className="text-white font-semibold">{selectedNFT.collection}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-steel-500">Edition</span>
                    <span className="text-white font-semibold">1 of 1</span>
                  </div>
                  <Button className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white font-bold">
                    <ExternalLink size={16} className="mr-2" />
                    View on OpenSea (Coming Soon)
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <section id="nfts" className="relative py-24 bg-gradient-to-b from-gunmetal-950 via-purple-950/20 to-gunmetal-950">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-full mb-6">
              <Sparkles className="text-purple-400" size={20} />
              <span className="text-purple-400 font-semibold uppercase tracking-wider text-sm">Exclusive Digital Art</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-orange-400">
                Genesis NFT Gallery
              </span>
            </h2>
            <p className="text-steel-400 max-w-2xl mx-auto text-lg">
              Unique Master Print 1-of-1 Collection. Genesis Minted. Stored in the SD Vault.
            </p>
          </div>

          {/* NFT Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {nftData.map((nft) => (
              <div
                key={nft.id}
                onClick={() => setSelectedNFT(nft)}
                className="group cursor-pointer"
                data-testid={`nft-card-${nft.id}`}
              >
                <div className={`relative rounded-2xl overflow-hidden border border-transparent bg-gradient-to-br ${getRarityColor(nft.rarity)} p-[2px] transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-purple-500/30`}>
                  <div className="bg-gunmetal-900 rounded-2xl overflow-hidden">
                    {/* Image */}
                    <div className="relative aspect-square overflow-hidden">
                      <img
                        src={nft.image}
                        alt={nft.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gunmetal-900 via-transparent to-transparent opacity-60" />
                      
                      {/* Rarity Badge */}
                      <div className={`absolute top-4 right-4 px-3 py-1 rounded-full border text-xs font-bold uppercase ${getRarityBg(nft.rarity)}`}>
                        {nft.rarity}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-white mb-2 line-clamp-1">{nft.title}</h3>
                      <p className="text-steel-400 text-sm line-clamp-2 mb-3">{nft.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-purple-400 text-xs font-semibold uppercase">{nft.collection}</span>
                        <span className="text-steel-500 text-xs">1 of 1</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-12">
            <p className="text-steel-400 mb-4">
              More exclusive drops coming soon
            </p>
            <Button className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white font-bold px-8 py-6 text-lg">
              <Sparkles className="mr-2" size={20} />
              Join the Waitlist
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};
